<template>
  <AutoComplete
    v-model="selectedOrganization"
    :suggestions="filteredOrganizations"
    @complete="searchOrganizations"
    optionLabel="organizationName"
    placeholder="Search Organizations"
    dropdown
    fluid
    forceSelection
    :virtualScrollerOptions="{ itemSize: 64 }"
    :panelClass="'organization-dropdown'"
    class="relative organizations-selector"
    data-test="organizations-selector"
    :loading="isLoading"
    :disabled="isLoading"
    ref="dropdown"
  >
    <template #option="slotProps">
      <Transition name="fade">
        <div
          class="flex items-center gap-4 hover:bg-gray-100 rounded-lg cursor-pointer transition-all duration-200"
          :data-test="'autocomplete-option-' + slotProps.option.organizations_id"
        >
          <img
            :src="fundraiserStore.orgLogoUrl(slotProps.option.organizations_id)"
            alt="Organization Logo"
            class="w-10 h-10 rounded-full object-cover"
          />
          <span class="text-gray-700 font-semibold">{{ slotProps.option.organizationName }}</span>
          <i
            :class="slotProps.selected ? 'pi pi-heart text-red-500' : 'pi pi-plus-circle text-gray-500'"
            class="ml-auto"
          ></i>
        </div>
      </Transition>
    </template>
  </AutoComplete>

  <!-- Selected organizations display -->
  <div
    v-if="selectedOrganizations.length"
    class="selected-organizations flex flex-col gap-2 my-4"
    :class="selectedContainerClass"
  >
    <div
      v-for="organizations_id in selectedOrganizations"
      :key="organizations_id"
      class="flex items-center gap-4 p-3 bg-white shadow-md rounded-lg border border-gray-200"
      :data-test="'selected-organization-' + organizations_id"
    >
      <img
        :src="fundraiserStore.orgLogoUrl(organizations_id)"
        alt="Organization Logo"
        class="w-10 h-10 rounded-full object-cover"
      />
      <div class="text-gray-700 font-semibold flex grow">
        {{ organizations.find((org) => org.organizations_id === organizations_id)?.organizationName }}
      </div>
      <Button
        @click.prevent="remove(organizations_id)"
        variant="text"
        class="text-red-500 cursor-pointer"
        :data-test="'remove-button-' + organizations_id"
      >
        <i class="fa fa-times"></i>
      </Button>
    </div>
  </div>
</template>

<script setup>
import { ref, defineEmits, onBeforeMount, watch, computed } from 'vue';
import { useFundraiserStore } from '@/stores/FundraiserStore.js';

const fundraiserStore = useFundraiserStore();

const organizations = ref([]);
const filteredOrganizations = ref([]);
const selectedOrganizations = ref([]);
const selectedOrganization = ref(null);
const dropdown = ref(null);
const isLoading = ref(true);

const props = defineProps({
  hidden: {
    type: Array,
    default: () => [],
  },
  selected: {
    type: Array,
    default: () => [],
  },
  selectedContainerClass: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['change']);

watch(props, ({ selected }) => {
  selectedOrganizations.value = selected ?? [];
});

watch(selectedOrganization, (organization) => {
  if (organization && typeof organization == 'object') {
    selectedOrganizations.value.push(organization.organizations_id);
    emit('change', selectedOrganizations.value);

    selectedOrganization.value = null;
    dropdown.value.writeValue(null);

    document.querySelector('.organizations-selector .p-autocomplete-input').setAttribute('value', '');
  }
});

const remove = (organizations_id) => {
  selectedOrganizations.value = selectedOrganizations.value.filter((orgId) => orgId !== organizations_id);
  emit('change', selectedOrganizations.value);
};

const searchOrganizations = (search) => {
  filteredOrganizations.value =
    unselectedOrganizations.value?.filter((org) =>
      org.organizationName.toLowerCase().includes(search.query.toLowerCase()),
    ) ?? [];
};

const unselectedOrganizations = computed(() => {
  const hidden = Array.isArray(props.hidden) ? props.hidden : [];
  const result = organizations.value.filter(
    (org) => !selectedOrganizations?.value.includes(org.organizations_id) && !hidden.includes(org.organizations_id),
  );
  return result;
});

onBeforeMount(async () => {
  await fundraiserStore.bringAllOrganizations();
  organizations.value = fundraiserStore.organizations.sort((a, b) =>
    a.organizationName.localeCompare(b.organizationName),
  );
  isLoading.value = false;
});
</script>
